"use client";

/**
 * Canonical public client login: /auth/login
 *
 * Two cookie paths (see auth path docs / bff-auth.ts):
 *
 * A) Portal Zitadel BFF (default sandbox):
 *    Browser POSTs to POST /api/auth/zitadel/client/login → sessionToken cookies.
 *    Next.js talks to Zitadel; clients never call IdP APIs directly.
 *
 * B) QTP BFF __Host-app_session (target cookie-only API path):
 *    When NEXT_PUBLIC_USE_QTP_BFF_AUTH=true, redirect to
 *    https://api.sandbox.mechagora.net/auth/start?return_to=<absolute SPA URL>
 *    Cookie is set on api.*; browser must call QTP with credentials: include.
 */
import React, { useEffect } from "react";
import { ClientLoginPage } from "@/domains/auth";
import { BFF_AUTH, resolveQtpBffBase } from "@/domains/auth/services/bff-auth";
import { ENV_CONFIG } from "@/infrastructure/config/environment";
import { SafetyOutlined } from "@ant-design/icons";
import { useTheme } from "@/shared/theme";
import "@/shared/theme/styles/login.css";

export default function AuthLoginPage() {
  const theme = useTheme();
  const themeClass = theme.isDark ? "dark" : "light";

  useEffect(() => {
    if (!ENV_CONFIG.USE_QTP_BFF_AUTH) return;
    const returnTo =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("return_to") ||
        "/client/dashboard"
        : "/client/dashboard";
    BFF_AUTH.redirectToLogin(resolveQtpBffBase(ENV_CONFIG.QTP_BFF_URL), returnTo);
  }, []);

  if (ENV_CONFIG.USE_QTP_BFF_AUTH) {
    return (
      <div
        className={`login-page ${themeClass}`}
        data-theme={themeClass}
        style={{
          fontFamily:
            "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          background: "var(--login-bg, var(--color-background, #F3EBE2))",
          color: "var(--login-text, var(--color-text-primary, #1F1612))",
        }}
      >
        <main
          role="status"
          aria-live="polite"
          style={{
            textAlign: "center",
            maxWidth: 360,
            padding: "28px 24px",
            borderRadius: 16,
            border: "1px solid var(--login-border, var(--color-border, #D4C0AE))",
            background: "var(--login-surface, var(--color-card, #FFFFFF))",
            boxShadow: "var(--login-shadow)",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 14,
              color: "var(--login-brand-strong, var(--color-primary, #8FB4C8))",
              fontWeight: 600,
              fontSize: "0.85rem",
            }}
          >
            <SafetyOutlined aria-hidden />
            Secure sign-in
          </div>
          <p
            style={{
              margin: 0,
              color: "var(--login-text, var(--color-text-primary, #1F1612))",
              fontSize: "1rem",
              fontWeight: 600,
            }}
          >
            Connecting to secure sign-in…
          </p>
          <p
            style={{
              margin: "10px 0 0",
              color: "var(--login-text-muted, var(--color-text-secondary, #4A3A32))",
              fontSize: "0.85rem",
              lineHeight: 1.5,
            }}
          >
            You will continue automatically. Keep this tab open.
          </p>
        </main>
      </div>
    );
  }

  return <ClientLoginPage />;
}
